import React, { useCallback, useContext, useMemo, useState } from 'react'

import HttpStatus from 'components/Common/HttpStatus'
import Error404Page from 'components/Pages/Error404Page'
import shouldShowGiftCards from 'lib/payment/shouldShowGiftCards'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HeadData from 'components/Common/HeadData'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Faq from 'components/Common/Faq'
import IconedCallout from 'components/Common/IconedCallout'
import Select from 'components/Common/Form/Input/Select'

import LoggedInOnly from 'components/Common/LoggedInOnly'
import GiftCardIcon from 'LuxKit/Icons/custom/GiftCardIcon'
import PrintGiftCardIcon from 'LuxKit/Icons/custom/PrintGiftCardIcon'
import MoneyIcon from 'LuxKit/Icons/custom/MoneyIcon'
import { last } from 'lib/array/arrayUtils'
import { formatDynamicAmount } from 'lib/format/formatCurrencyIntl'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'

import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { generateGiftCardItem } from 'lib/checkout/gift/cart'
import TextInput from 'components/Common/Form/Input/TextInput'
import { formToObject } from 'lib/forms/formToObject'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import ModalContext from 'contexts/ModalContext'
import RedeemGiftCard from 'components/Common/RedeemGiftCard/RedeemGiftCard'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import HotelOfferCarousel from 'home/components/OfferCarousels/HotelOfferCarousel'
import ExperienceOfferCarousel from 'home/components/OfferCarousels/ExperienceOfferCarousel/ExperienceOfferCarousel'
import Places from 'constants/places'
import FormatCurrency from 'components/Common/FormatCurrency'

const BuyPaneBody = styled(PaneBody)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(80)};
    padding-bottom: ${rem(80)};
  }
`

const ThreeColumnGrid = styled.div`
  display: grid;
  gap: ${rem(80)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(48)};
    grid-template-columns: repeat(3, 1fr);
  }
`

const FormContainer = styled.div`
  display: grid;
  gap: ${rem(32)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 3fr 2fr;
  }
`

const HowItWorksContainer = styled.div`
  padding: ${rem(40)} 0;

  > * + * {
    margin-top: ${rem(80)};
  }
`

interface Props {
  amountOptions: Array<number>
  currency: string
  regionCode: string
}

interface FormData {
  giftCardOtherValue?: string
  giftCardValue: string
}

function GiftCardsPage(props: Props) {
  const { regionCode, currency, amountOptions } = props
  const [otherAmount, setOtherAmount] = useState(false)
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const intl = useIntl()

  const onProceedToCheckout = useCallback<React.FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    const giftCardData = formToObject<FormData>(e.currentTarget)
    const selectedAmount = parseInt(giftCardData.giftCardOtherValue ?? giftCardData.giftCardValue)
    const giftCardItem = generateGiftCardItem(selectedAmount)
    dispatch(addToCheckoutCart([giftCardItem]))
    Analytics.trackEvent(addToCart())
    dispatch(navigateToCheckout())
  }, [dispatch])

  const howItWorksItems = useMemo(() => [
    {
      title: intl.formatMessage({
        id: '8bDYvmxWkF',
        defaultMessage: 'Purchase online ',
        description: 'How it works item title for purchasing gift card online',
      }),
      text: intl.formatMessage({
        id: 'Tpe9B/pOwI',
        defaultMessage: 'Simply choose your Gift Card value, from {minAmount} up to {maxAmount}, and select the Buy Now button.',
        description: 'How it works item text explaining gift card purchase process',
      }, {
        minAmount: <FormatCurrency value={amountOptions[0]} />,
        maxAmount: <FormatCurrency value={last(amountOptions)} />,
      }),
      icon: <GiftCardIcon size={68} />,
    },
    {
      title: intl.formatMessage({
        id: 'nTY7ap6y+X',
        defaultMessage: 'Receive by email',
        description: 'How it works item title for receiving gift card by email',
      }),
      text: intl.formatMessage({
        id: 'lNUSLjGkxi',
        defaultMessage: 'The Gift Card will be sent by email for you to print or forward to the lucky recipient.',
        description: 'How it works item text explaining gift card email delivery',
      }),
      icon: <PrintGiftCardIcon size={68} />,
    },
    {
      title: intl.formatMessage({
        id: '2/uehEbuWs',
        defaultMessage: 'The world is their oyster',
        description: 'How it works item title about gift card redemption',
      }),
      text: intl.formatMessage({
        id: 'Rw/8PuZIsu',
        defaultMessage: 'The recipient enters their unique code at checkout, and the purchased value will be applied to their order.',
        description: 'How it works item text explaining gift card redemption process',
      }),
      icon: <MoneyIcon size={68} />,
    },
  ], [amountOptions, intl])

  const onAmountSelect = useCallback((e: React.FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.value === '-1') {
      return setOtherAmount(true)
    }

    setOtherAmount(false)
  }, [])

  const openGiftCardModal = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'open_redeem_gift_card',
      action: 'clicked',
      category: AnalyticsPage.giftCards,
      type: 'interaction',
    })
    showModal(
      <AnalyticsPageContext.Provider value={AnalyticsPage.giftCards}>
        <RedeemGiftCard />
      </AnalyticsPageContext.Provider>,
    )
  }, [showModal])

  if (!shouldShowGiftCards(currency) || !config.giftCards) {
    return (
      <HttpStatus code={404}>
        <Error404Page invalidRegion />
      </HttpStatus>
    )
  }

  return (
    <>
      <HeadData
        title={intl.formatMessage({
          id: 'xmY6qgzeTz',
          defaultMessage: '{brandTitle} Gift Cards & Vouchers',
          description: 'Page title for gift cards page in browser tab',
        }, { brandTitle: config.title })}
        description={intl.formatMessage({
          id: 'HKHZcioBN+',
          defaultMessage: "Treat them to something they really want with a {brandTitle} Gift Card. From local weekends away to once-in-a-lifetime getaways in exotic locales, they'll have the world at their fingertips with great deals to amazing places.",
          description: 'Meta description for gift cards page for SEO',
        }, { brandTitle: config.title })}
      />
      <Pane>
        <BuyPaneBody size="md">
          <LayoutContainer>
            <FormContainer>
              <ResponsiveImage id={config.giftCards.imageId} />
              <form onSubmit={onProceedToCheckout}>
                <VerticalSpacer gap={24}>
                  <Heading variant="heading2" as="h1">
                    {intl.formatMessage({
                      id: 'QJfGJ8dIfh',
                      defaultMessage: '{brandTitle} Gift Card',
                      description: 'Main heading for gift card purchase form',
                    }, {
                      brandTitle: config.title,
                    })}
                  </Heading>
                  <BodyText variant="large">
                    {intl.formatMessage({
                      id: '5BmnrpUrSG',
                      defaultMessage: 'Give them the world in one pocket-sized present with a {brandTitle} Gift Card. With incredible offers in destinations near and far, the world is theirs to explore, and a gift card makes it all possible.',
                      description: 'Description text for gift card purchase form',
                    }, {
                      brandTitle: config.title,
                    })}
                  </BodyText>
                  <LoggedInOnly
                    buttonText={intl.formatMessage({
                      id: 's3J9bjmA5t',
                      defaultMessage: 'Log in to buy or redeem',
                      description: 'Button text for login prompt on gift card page',
                    })}
                    buttonProps={{ fit: 'flex' }}
                  >
                    <VerticalSpacer gap={8}>
                      <Select
                        id="gift-card-value"
                        label={intl.formatMessage({
                          id: 'eb3J1fDE8s',
                          defaultMessage: 'Select card value',
                          description: 'Label for gift card value select dropdown',
                        })}
                        name="giftCardValue"
                        onChange={onAmountSelect}
                        defaultValue={amountOptions[0]}
                        data-testid="gift-card-value-select"
                        required
                      >
                        {amountOptions.map((amount) =>
                          <option key={amount} value={amount}>
                            <FormatCurrency value={amount} />
                          </option>)}
                        <option value="-1">
                          {intl.formatMessage({
                            id: 'pcQ0CibOTJ',
                            defaultMessage: 'Other amount',
                            description: 'Option text for other amount in gift card value select',
                          })}
                        </option>
                      </Select>
                      {otherAmount && (
                        <TextInput
                          label={intl.formatMessage({
                            id: 'Dg7XX5mHg2',
                            defaultMessage: 'Enter your amount',
                            description: 'Label for custom gift card amount input',
                          })}
                          placeholder={`Min ${formatDynamicAmount(amountOptions[0], currency, regionCode)} - Max ${formatDynamicAmount(last(amountOptions), currency, regionCode)}`}
                          required
                          type="number"
                          name="giftCardOtherValue"
                          min={amountOptions[0]}
                          max={last(amountOptions)}
                        />
                      )}
                      <TextButton kind="primary" fit="full-width" type="submit" data-testid="gift-card-add-to-cart">
                        {intl.formatMessage({
                          id: '9FVvmN8PAm',
                          defaultMessage: 'Buy now',
                          description: 'Button text to purchase gift card',
                        })}
                      </TextButton>
                    </VerticalSpacer>
                    {(isSpoofed || !config.ENABLE_GIFT_CARD_V2) && (
                      <VerticalSpacer gap={8}>
                        <Heading variant="heading6">
                          {intl.formatMessage({
                            id: 'uPNhs5aedC',
                            defaultMessage: 'Ready to redeem a gift card?',
                            description: 'Heading prompting user to redeem a gift card',
                          })}
                        </Heading>
                        <TextButton kind="secondary" fit="full-width" onClick={openGiftCardModal}>
                          {intl.formatMessage({
                            id: 'KP9lSNlYRN',
                            defaultMessage: 'Redeem a gift card',
                            description: 'Button text to open gift card redemption modal',
                          })}
                        </TextButton>
                      </VerticalSpacer>
                    )}
                  </LoggedInOnly>
                </VerticalSpacer>
              </form>
            </FormContainer>
          </LayoutContainer>
        </BuyPaneBody>
      </Pane>
      <Pane background="neutral-six">
        <LayoutContainer>
          <HowItWorksContainer>
            <Heading variant="heading3" align="center" as="h2">
              {intl.formatMessage({
                id: '+s5av4ytiY',
                defaultMessage: 'How It Works',
                description: 'Heading for how it works section',
              })}
            </Heading>
            <ThreeColumnGrid>
              {howItWorksItems.map((item, i) =>
                <IconedCallout
                  key={i}
                  icon={item.icon}
                  title={item.title}
                  text={item.text}
                />,
              )}
            </ThreeColumnGrid>
            <BodyText variant="large" align="center">
              {intl.formatMessage({
                id: '2fyK68guDC',
                defaultMessage: 'Read our full {termsLink}',
                description: 'Text with link to terms and conditions',
              }, {
                termsLink: (() => {
                  const termsText = intl.formatMessage({
                    id: '49ZZKj9vpT',
                    defaultMessage: 'Terms & Conditions',
                    description: 'Link text for terms and conditions',
                  })
                  if (config.giftCards.externalTermsAndConditions) {
                    return (
                      <TextLink weight="regular" href={config.giftCards.externalTermsAndConditions}>
                        {termsText}
                      </TextLink>
                    )
                  }
                  return <TextLink weight="regular" to="/giftcards-terms">{termsText}</TextLink>
                })(),
              })}
            </BodyText>
          </HowItWorksContainer>
        </LayoutContainer>
      </Pane>
      <HotelOfferCarousel
        title={intl.formatMessage({
          id: 'wW/itf/jxP',
          defaultMessage: 'Gift escapes with more',
          description: 'Carousel title for gift card offers',
        })}
        tileStyle="card-default"
      />
      <ExperienceOfferCarousel
        place={Places.Anywhere}
        experienceHeading={intl.formatMessage({
          id: 'OdNBaFTZTO',
          defaultMessage: 'Give the gift of unforgettable experience',
          description: 'Heading for experience carousel on gift card page',
        })}
      />
      <Pane>
        <PaneBody size="xl-y md">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading variant="heading3" align="center" as="h2">
                {intl.formatMessage({
                  id: '9ocxf9Ax18',
                  defaultMessage: 'Frequently Asked Questions',
                  description: 'Heading for FAQ section',
                })}
              </Heading>
              <Faq slug={config.giftCards.faqSlug} />
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  currency: state.geo.currentCurrency,
  amountOptions: state.giftCard.giftCardOptions,
  regionCode: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(GiftCardsPage)
